import React from 'react';
import type { monaco } from 'react-monaco-editor';
import { EditorProps } from '../EditorV3';
import registerPythonCompletions from '../languages/python';
import registerStarlarkCompletions from '../languages/starlark';
import registerSqlCompletions from '../languages/sql';
import {
  registerPantherFlowCompletions,
  registerPantherFlowInlayHints,
  registerHover as registerPantherFlowHover,
  registerPantherFlowSignatureHelp,
} from '../languages/pantherflow';

const noop = () => {};

const getLanguageCompletionsRegistration = (language: EditorProps['language']) => {
  switch (language) {
    case 'sql':
      return registerSqlCompletions;
    case 'python':
      return registerPythonCompletions;
    case 'pantherflow':
      return registerPantherFlowCompletions;
    case 'starlark':
      return registerStarlarkCompletions;
    default:
      return () => ({ dispose: noop });
  }
};

const getLanguageInlayHintsRegistration = (language: EditorProps['language']) => {
  switch (language) {
    case 'pantherflow':
      return registerPantherFlowInlayHints;
    default:
      return () => ({ dispose: noop });
  }
};

const getLanguageHoverRegistration = (language: EditorProps['language']) => {
  switch (language) {
    case 'pantherflow':
      return registerPantherFlowHover;
    default:
      return () => ({ dispose: noop });
  }
};

const getLanguageSignatureHelpRegistration = (language: EditorProps['language']) => {
  switch (language) {
    case 'pantherflow':
      return registerPantherFlowSignatureHelp;
    default:
      return () => ({ dispose: noop });
  }
};

export const getTextModelOptions = (
  language: EditorProps['language'],
): monaco.editor.ITextModelUpdateOptions => {
  switch (language) {
    case 'python':
    case 'starlark':
      return { tabSize: 4, insertSpaces: true };
    case 'json':
    case 'yaml':
    default:
      return { tabSize: 2, insertSpaces: true };
  }
};

interface UseEditorLanguageProps {
  monacoInstance?: typeof monaco;
  language: EditorProps['language'];
  disableAutocomplete?: boolean;
}

const useEditorLanguage = ({
  monacoInstance,
  language,
  disableAutocomplete,
}: UseEditorLanguageProps) => {
  React.useEffect(() => {
    if (!monacoInstance || !language) {
      return noop;
    }

    const textModelOptions = getTextModelOptions(language);
    const languageModel = monacoInstance.editor
      .getModels()
      .find(model => model.getLanguageId() === language);

    if (languageModel) {
      languageModel.updateOptions(textModelOptions);
    }

    const hintsRegistration = getLanguageInlayHintsRegistration(language)(monacoInstance);
    if (disableAutocomplete) {
      return hintsRegistration.dispose;
    }

    const registration = getLanguageCompletionsRegistration(language)(monacoInstance);
    const hover = getLanguageHoverRegistration(language)(monacoInstance);
    const sigHelp = getLanguageSignatureHelpRegistration(language)(monacoInstance);
    return () => {
      registration.dispose();
      hintsRegistration.dispose();
      hover.dispose();
      sigHelp.dispose();
    };
  }, [monacoInstance, language, disableAutocomplete]);
};

export default useEditorLanguage;
